import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { serviceAreas, OFFICE_POSITION } from './serviceAreas';

const officeLocation = { lat: OFFICE_POSITION[0], lng: OFFICE_POSITION[1] };

// Same icon style as Precision Cleaning project — green default, red active
// Green pin shown on page load for all locations
const greenIcon = L.divIcon({
  className: 'custom-marker service-area-pin-green',
  html: `<div style="position:relative;width:30px;height:40px;">
    <svg viewBox="0 0 24 36" width="30" height="40" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#2e7d32"/>
      <circle cx="12" cy="11" r="5" fill="#fff"/>
    </svg>
  </div>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -35],
});

// Red pin when highlighted from sidebar (with tooltip/popup above)
const redIcon = L.divIcon({
  className: 'custom-marker service-area-pin-red',
  html: `<div style="position:relative;width:36px;height:46px;">
    <svg viewBox="0 0 24 36" width="36" height="46" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#e53935"/>
      <circle cx="12" cy="11" r="5" fill="#fff"/>
    </svg>
  </div>`,
  iconSize: [36, 46],
  iconAnchor: [18, 46],
  popupAnchor: [0, -41],
});



const ServiceAreaMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const [selectedArea, setSelectedArea] = useState(null); // null = Show All (all green)

  const handleLocationClick = (area) => {
    const lat = area.position ? area.position[0] : area.lat;
    const lng = area.position ? area.position[1] : area.lng;
    setSelectedArea(area.name);
    if (mapInstanceRef.current) {
      // Same zoom logic as Precision Cleaning: flyTo with zoom 11 and highlight red with popup
      // Office (Leicester) zooms a bit closer like precision office zoom 14
      const targetZoom = area.isOffice ? 14 : 11;
      mapInstanceRef.current.flyTo([lat, lng], targetZoom, { duration: 1.5 });
      mapInstanceRef.current.invalidateSize();
    }
  };

  const handleShowAll = () => {
    setSelectedArea(null);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([officeLocation.lat, officeLocation.lng], 9, { duration: 1.5 });
      mapInstanceRef.current.invalidateSize();
    }
  };

  // Init leaflet map — same as Precision Cleaning website (L.map, L.tileLayer, L.marker)
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current).setView([officeLocation.lat, officeLocation.lng], 9);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Create markers for all service areas — all green by default (like Precision Cleaning)
    serviceAreas.forEach((area) => {
      const [lat, lng] = area.position;
      const popupContent = area.isOffice
        ? '<strong>Precision Driving School</strong><br/>4 Rivers Street, Leicester, LE3 5RX'
        : `<strong>${area.name}</strong><br/>Service Area`;
      const marker = L.marker([lat, lng], { icon: greenIcon })
        .addTo(map)
        .bindPopup(popupContent);
      // Also bind tooltip for hover (green pins show name on hover)
      marker.bindTooltip(area.name, { direction: 'top', offset: [0, -35] });
      marker.on('click', () => handleLocationClick(area));
      markersRef.current[area.name] = marker;
    });

    mapInstanceRef.current = map;

    // cleanup on unmount
    return () => {
      // map.remove() handled if needed
    };
  }, []);

  // When sidebar selection changes — highlight red pin + show tooltip/popup above pin with zoom
  // Same effect as Precision Cleaning: setIcon + openPopup/closePopup
  useEffect(() => {
    const markers = markersRef.current;
    if (!mapInstanceRef.current || Object.keys(markers).length === 0) return;

    Object.keys(markers).forEach((key) => {
      const marker = markers[key];
      const isActive = key === selectedArea;
      marker.setIcon(isActive ? redIcon : greenIcon);
      if (isActive) {
        // Show tooltip/popup above the red pin — same as precision cleaning
        marker.openPopup();
        // also ensure permanent tooltip look: unbind hover tooltip and bind permanent one with details
        // Keep popup as main tooltip above pin (precision uses popup)
      } else {
        marker.closePopup();
      }
    });
  }, [selectedArea]);

  return (
    <section className="service-area-section" id="service-area-map">
      <div className="auto-container">
        <div className="sec-title centred">
          <h2>Our Service Areas</h2>
          <p>We provide driving lessons across Leicester and surrounding areas</p>
        </div>
        <div className="service-area-wrapper">
          <div className="service-area-sidebar">
            <button
              className={`service-area-btn ${selectedArea === null ? 'active' : ''}`}
              onClick={handleShowAll}
            >
              <i className="bi bi-geo-alt-fill"></i>
              <span>Show All Locations</span>
            </button>
            {serviceAreas.map((area) => (
              <button
                key={area.name}
                className={`service-area-btn ${selectedArea === area.name ? 'active' : ''} ${area.isOffice ? 'is-office' : ''}`}
                onClick={() => handleLocationClick(area)}
              >
                <i className={`bi ${area.isOffice ? 'bi-building' : 'bi-geo-alt-fill'}`}></i>
                <div className="area-info">
                  <span className="area-name">{area.name}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="service-area-map">
            {/* Raw Leaflet container like Precision Cleaning (div with ref) */}
            <div ref={mapRef} style={{ height: '450px', width: '100%', zIndex: 1 }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
